import { create } from 'zustand'
import { clearSession, setAccessToken } from './tokenStore'
import type { UserResponse } from '@/shared/api/types'

export type CurrentUser = UserResponse

interface SessionState {
  status: 'unknown' | 'anonymous' | 'authenticated'
  user: CurrentUser | null
  authorities: string[]
  fingerprint: string | null
  setAuthenticated: (token: string, user: CurrentUser, authorities: string[], fingerprint?: string) => void
  setAnonymous: () => void
  patchUser: (patch: Partial<CurrentUser>) => void
  clear: () => void
}

export const useSession = create<SessionState>((set) => ({
  status: 'unknown',
  user: null,
  authorities: [],
  fingerprint: null,
  setAuthenticated: (token, user, authorities, fingerprint) => {
    setAccessToken(token)
    set({ status: 'authenticated', user, authorities, fingerprint: fingerprint ?? null })
  },
  setAnonymous: () => set({ status: 'anonymous', user: null, authorities: [], fingerprint: null }),
  patchUser: (patch) => set((s) => ({ user: s.user ? { ...s.user, ...patch } : s.user })),
  clear: () => {
    clearSession()
    set({ status: 'anonymous', user: null, authorities: [], fingerprint: null })
  },
}))

/**
 * The ONLY permission check in the app.
 *
 * Authorities come from GET /rbac/me/authorities — the server is the authority.
 * The backend EXPANDS a `*:*` grant to the full catalogue before returning it,
 * so a super-user arrives holding every concrete authority string rather than a
 * wildcard. The wildcard branches below are belt-and-braces for the case where
 * that expansion is ever moved client-side.
 */
export function can(authorities: string[], permission?: string): boolean {
  if (!permission) return true
  if (authorities.includes('*:*')) return true
  if (authorities.includes(permission)) return true
  const [module] = permission.split(':')
  return authorities.includes(`${module}:*`)
}

export function useCan(permission?: string): boolean {
  return can(useSession((s) => s.authorities), permission)
}
